import { Inject, Injectable, InjectionToken } from '@angular/core';
import { PaginatedStateEffects } from '@shared/ui';
import type {
  IAnyCasesForCaseTypeQuery,
  ICountCaseTypesQuery,
  ICountCasesByCaseTypeQuery,
  ICountWorkspacesByCaseTypeQuery,
  IDeleteCaseTypeCommand,
  IListCaseTypesQuery,
} from '@protocols/use-cases';
import { from, merge, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { CaseTypeData, CaseTypeState, CaseTypeStateModel } from '../state/case-type.state';

export const COUNT_CASE_TYPES_QUERY = new InjectionToken<ICountCaseTypesQuery>('COUNT_CASE_TYPES_QUERY');
export const LIST_CASE_TYPES_QUERY = new InjectionToken<IListCaseTypesQuery>('LIST_CASE_TYPES_QUERY');
export const DELETE_CASE_TYPE_COMMAND = new InjectionToken<IDeleteCaseTypeCommand>('DELETE_CASE_TYPE_COMMAND');
export const COUNT_CASES_BY_CASE_TYPE_QUERY = new InjectionToken<ICountCasesByCaseTypeQuery>(
  'COUNT_CASES_BY_CASE_TYPE_QUERY'
);
export const COUNT_WORKSPACES_BY_CASE_TYPE_QUERY = new InjectionToken<ICountWorkspacesByCaseTypeQuery>(
  'COUNT_WORKSPACES_BY_CASE_TYPE_QUERY'
);
export const ANY_CASES_FOR_CASE_TYPE_QUERY = new InjectionToken<IAnyCasesForCaseTypeQuery>(
  'ANY_CASES_FOR_CASE_TYPE_QUERY'
);

@Injectable()
export class CaseTypeEffects extends PaginatedStateEffects<CaseTypeData, CaseTypeStateModel> {
  constructor(
    protected readonly caseTypeState: CaseTypeState,
    @Inject(COUNT_CASE_TYPES_QUERY) private readonly countCaseTypesQuery: ICountCaseTypesQuery,
    @Inject(LIST_CASE_TYPES_QUERY) private readonly listCaseTypesQuery: IListCaseTypesQuery,
    @Inject(DELETE_CASE_TYPE_COMMAND) private readonly deleteCaseTypeCommand: IDeleteCaseTypeCommand,
    @Inject(COUNT_CASES_BY_CASE_TYPE_QUERY) private readonly countCasesByCaseTypeQuery: ICountCasesByCaseTypeQuery,
    @Inject(COUNT_WORKSPACES_BY_CASE_TYPE_QUERY)
    private readonly countWorkspacesByCaseTypeQuery: ICountWorkspacesByCaseTypeQuery,
    @Inject(ANY_CASES_FOR_CASE_TYPE_QUERY) private readonly anyCasesForCaseTypeQuery: IAnyCasesForCaseTypeQuery
  ) {
    super(caseTypeState);
  }

  // ============================================================================
  // Backwards-compat selector aliases
  // ============================================================================

  public readonly caseTypes$ = this.items$;
  public readonly nextCaseTypeToken$ = this.nextToken$;
  public readonly previousCaseTypeTokens$ = this.previousTokens$;
  public readonly caseTypesLoaded$ = this.itemsLoaded$;
  public readonly noCaseTypes$ = this.noItems$;
  public readonly selectedCaseTypeId$ = this.caseTypeState.selectedCaseTypeId$;
  public readonly selectedCaseType$ = this.caseTypeState.selectedCaseType$;

  // ============================================================================
  // Backwards-compat effect aliases
  // ============================================================================

  public readonly deleteCaseType = this.deleteItem;
  public readonly loadMoreCaseTypes = this.loadMoreItems;
  public readonly initializeCaseTypes = this.initializeItems;
  public readonly countCaseTypes = this.countItems;

  // ============================================================================
  // Updaters
  // ============================================================================

  public readonly setSearchTerm = this.caseTypeState.setSearchTerm;
  public readonly changePageSize = this.caseTypeState.changePageSize;

  public readonly selectCaseType = this.caseTypeState.updater((state, caseTypeId: string | undefined) => ({
    ...state,
    selectedCaseTypeId: caseTypeId,
  }));

  // ============================================================================
  // Specific methods
  // ============================================================================

  public canBeDeleted(item: CaseTypeData): Observable<boolean> {
    return from(this.anyCasesForCaseTypeQuery.execute(item.id)).pipe(map((any) => !any));
  }

  // ============================================================================
  // Abstract method implementations
  // ============================================================================

  protected async countRepository(searchTerm?: string): Promise<number> {
    return this.countCaseTypesQuery.execute(searchTerm);
  }

  protected async deleteRepositoryItem(itemId: string): Promise<boolean> {
    try {
      await this.deleteCaseTypeCommand.execute(itemId);
      return true;
    } catch {
      return false;
    }
  }

  protected listFlow(
    limit: number,
    lastItem: CaseTypeData | undefined,
    searchTerm: string | undefined
  ): Observable<CaseTypeData[]> {
    return from(
      this.listCaseTypesQuery.execute({
        searchTerm,
        limit,
        lastItemName: lastItem?.name,
      })
    ).pipe(
      map((caseTypes) =>
        caseTypes.map((caseType) => ({
          ...caseType,
          usedByWorkspaces: undefined,
          usedByCases: undefined,
        }))
      )
    );
  }

  protected processDeferredAttributes(itemId: string): Observable<Partial<CaseTypeData>> {
    const workspaces$ = this.countAttribute(this.countWorkspacesByCaseTypeQuery.execute(itemId), itemId).pipe(
      map((usedByWorkspaces) => ({ usedByWorkspaces }))
    );
    const cases$ = this.countAttribute(this.countCasesByCaseTypeQuery.execute(itemId), itemId).pipe(
      map((usedByCases) => ({ usedByCases }))
    );
    return merge(workspaces$, cases$);
  }
}
